export type MeResponse = {
  user: { id: string; email: string }
  microsoftLinked: boolean
  microsoft: { email: string; linkedAt: number } | null
}

export type Mailbox = {
  id: string
  smtp: string
  display_name: string | null
  verified_at: number
}

export type MessageSummary = {
  id: string
  subject: string
  fromName: string | null
  fromAddress: string | null
  receivedAt: string
  isRead: boolean
  preview: string
  hasAttachments: boolean
}

export type MessageDetail = {
  id: string
  subject: string
  fromName: string | null
  fromAddress: string | null
  to: string[]
  receivedAt: string
  isRead: boolean
  hasAttachments: boolean
  bodyText: string
}

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = (body as { error?: string }).error ?? `http_${res.status}`
    throw new Error(err)
  }
  return body as T
}

export const api = {
  async requestPin(email: string): Promise<void> {
    const res = await fetch('/api/auth/request-pin', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    })
    await json<{ ok: true }>(res)
  },

  async verifyPin(email: string, pin: string): Promise<{ email: string }> {
    const res = await fetch('/api/auth/verify-pin', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, pin })
    })
    return json<{ ok: true; email: string }>(res)
  },

  async me(): Promise<MeResponse | null> {
    const res = await fetch('/api/me', { credentials: 'include' })
    if (res.status === 401) return null
    return json<MeResponse>(res)
  },

  async logout(): Promise<void> {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
  },

  microsoftStartUrl: '/api/auth/microsoft/start',

  async beginMicrosoftLink(): Promise<string> {
    const res = await fetch('/api/auth/microsoft/start', {
      headers: { accept: 'application/json' },
      credentials: 'include'
    })
    const data = await json<{ url: string }>(res)
    return data.url
  },

  async microsoftUnlink(): Promise<void> {
    const res = await fetch('/api/auth/microsoft/unlink', {
      method: 'POST',
      credentials: 'include'
    })
    await json<{ ok: true }>(res)
  },

  async listMailboxes(): Promise<Mailbox[]> {
    const res = await fetch('/api/mailboxes', { credentials: 'include' })
    const data = await json<{ mailboxes: Mailbox[] }>(res)
    return data.mailboxes
  },

  async addMailbox(smtp: string, displayName?: string): Promise<Mailbox> {
    const res = await fetch('/api/mailboxes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ smtp, displayName })
    })
    const data = await json<{ mailbox: Mailbox }>(res)
    return data.mailbox
  },

  async deleteMailbox(id: string): Promise<void> {
    const res = await fetch(`/api/mailboxes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    await json<{ ok: true }>(res)
  },

  async listMessages(smtp: string, top = 25): Promise<MessageSummary[]> {
    const res = await fetch(
      `/api/graph/mailboxes/${encodeURIComponent(smtp)}/messages?top=${top}`,
      { credentials: 'include', cache: 'no-store' }
    )
    const data = await json<{ messages: MessageSummary[] }>(res)
    return data.messages
  },

  async readMessage(smtp: string, id: string): Promise<MessageDetail> {
    const res = await fetch(
      `/api/graph/mailboxes/${encodeURIComponent(smtp)}/messages/${encodeURIComponent(id)}`,
      { credentials: 'include' }
    )
    return json<MessageDetail>(res)
  },

  async purgeMailbox(smtp: string): Promise<{
    smtp: string
    results: { inbox: number; sentitems: number; deleteditems: number }
    total: number
    timedOut: boolean
    message?: string
  }> {
    const res = await fetch(`/api/graph/mailboxes/${encodeURIComponent(smtp)}/purge`, {
      method: 'POST',
      credentials: 'include'
    })
    return json(res)
  },

  async deleteMessage(smtp: string, id: string): Promise<void> {
    const res = await fetch(
      `/api/graph/mailboxes/${encodeURIComponent(smtp)}/messages/${encodeURIComponent(id)}/delete`,
      { method: 'POST', credentials: 'include' }
    )
    await json<{ ok: true }>(res)
  },

  async mailboxCounts(): Promise<
    Record<
      string,
      {
        total: number
        unread: number
        sizeBytes: number | null
        sizeComputedAt: number | null
      } | null
    >
  > {
    const res = await fetch('/api/graph/mailbox-counts', {
      credentials: 'include',
      cache: 'no-store'
    })
    const data = await json<{
      counts: Record<
        string,
        {
          total: number
          unread: number
          sizeBytes: number | null
          sizeComputedAt: number | null
        } | null
      >
    }>(res)
    return data.counts
  },

  async refreshMailboxSize(smtp: string): Promise<{
    smtp: string
    sizeBytes?: number
    sizeComputedAt?: number
    messages: number
    timedOut: boolean
    partialBytes?: number
  }> {
    const res = await fetch(
      `/api/graph/mailboxes/${encodeURIComponent(smtp)}/refresh-size`,
      { method: 'POST', credentials: 'include' }
    )
    return json(res)
  },

  async searchUsers(q: string): Promise<Array<{ id: string; displayName: string; mail: string; upn: string }>> {
    const res = await fetch(
      `/api/graph/users/search?q=${encodeURIComponent(q)}`,
      { credentials: 'include' }
    )
    const data = await json<{ users: Array<{ id: string; displayName: string; mail: string; upn: string }> }>(res)
    return data.users
  },

  async discoverMailboxes(): Promise<{
    scanned: number
    truncated: boolean
    matches: Array<{ mail: string; displayName: string }>
  }> {
    const res = await fetch('/api/graph/discover-mailboxes', {
      method: 'POST',
      credentials: 'include'
    })
    return json(res)
  },

  async setMessageRead(smtp: string, id: string, isRead: boolean): Promise<void> {
    const res = await fetch(
      `/api/graph/mailboxes/${encodeURIComponent(smtp)}/messages/${encodeURIComponent(id)}/read`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isRead })
      }
    )
    await json<{ ok: true }>(res)
  }
}
